/* Central Umami owner. See the analytics contract and store handbook before adding events. */
(() => {
  const config = document.getElementById('HnAnalytics');
  if (!config || window.HNAnalytics) return;
  const host = window.location.hostname;
  const query = new URLSearchParams(window.location.search);
  const excluded = /\/(account|checkouts?|orders)(\/|$)/;
  if (!['heartsnotes.com', 'www.heartsnotes.com'].includes(host) ||
      window.Shopify?.designMode || window.Shopify?.theme?.role !== 'main' ||
      query.has('preview_theme_id') || excluded.test(window.location.pathname)) return;
  let scriptURL;
  try {
    scriptURL = new URL(config.dataset.tracker);
    if (scriptURL.protocol !== 'https:' || scriptURL.username || scriptURL.password || scriptURL.search || scriptURL.hash) return;
  } catch (_) { return; }
  if (!/^[\da-f]{8}(-[\da-f]{4}){3}-[\da-f]{12}$/i.test(config.dataset.website)) return;

  const properties = {
    product_view: ['product_id'], collection_view: ['collection_id'],
    variant_selected: ['variant_id'], add_to_cart: ['variant_id'],
    checkout_clicked: ['source'], preview_requested: ['product_id'],
    sale_alert_subscribed: [], navigation_clicked: ['destination'],
  };
  let ready = false;
  let loading = false;
  let viewed = false;
  let queue = [];
  function allowed() {
    try {
      return window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true &&
        !['1', 'yes'].includes(String(navigator.doNotTrack || window.doNotTrack)) &&
        !excluded.test(window.location.pathname);
    } catch (_) { return false; }
  }
  function cleanURL(value, campaign = false) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (!['https:', 'http:'].includes(url.protocol) || excluded.test(url.pathname)) return '';
      const params = new URLSearchParams();
      if (campaign) ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(key => {
        const value = url.searchParams.get(key);
        if (value && /^[a-z0-9_. -]{1,100}$/i.test(value)) params.set(key, value);
      });
      // Referrers retain the source domain only; page URLs retain public paths.
      return campaign ? `${url.pathname}${params.size ? `?${params}` : ''}` : url.origin;
    } catch (_) { return ''; }
  }
  function cleanData(name, data = {}) {
    const result = {};
    properties[name]?.forEach(key => {
      const value = String(data[key] ?? '');
      if (key.endsWith('_id') && /^\d{1,20}$/.test(value)) result[key] = value;
      if (key === 'source' && ['cart', 'buy_now'].includes(value)) result[key] = value;
      if (key === 'destination' && ['books', 'product', 'collection', 'audio', 'quiz', 'magazine', 'sales'].includes(value)) result[key] = value;
    });
    return result;
  }
  // This second gate also stops sends from an already-loaded tracker after withdrawal.
  window.HNAnalyticsBeforeSend = (type, payload) => {
    if (!allowed() || type !== 'event' || (payload.name && !Object.hasOwn(properties, payload.name))) return false;
    return {
      website: config.dataset.website, hostname: host,
      screen: payload.screen, language: payload.language,
      title: config.dataset.pageType,
      url: cleanURL(window.location.href, true), referrer: cleanURL(document.referrer),
      ...(payload.name ? {name: payload.name, data: cleanData(payload.name, payload.data)} : {}),
    };
  };
  function send(name, data) {
    try {
      const result = name ? window.umami.track(name, data) : window.umami.track();
      result?.catch?.(() => {});
    } catch (_) { /* Analytics must never interrupt commerce. */ }
  }
  function track(name, data) {
    if (!allowed() || !Object.hasOwn(properties, name)) return;
    const event = [name, cleanData(name, data)];
    if (ready) send(...event);
    else if (queue.length < 20) queue.push(event);
  }
  window.HNAnalytics = Object.freeze({track});
  function pageview() {
    if (viewed || !allowed()) return;
    viewed = true;
    send();
    if (config.dataset.product) track('product_view', {product_id: config.dataset.product});
    if (config.dataset.collection) track('collection_view', {collection_id: config.dataset.collection});
  }
  function start() {
    if (!allowed()) { queue = []; return; }
    if (ready) { pageview(); return; }
    if (loading) return;
    // Never attach a second tracker over an unknown integration.
    if (window.umami) return;
    loading = true;
    const script = document.createElement('script');
    script.src = scriptURL.href;
    script.defer = true;
    script.setAttribute('data-website-id', config.dataset.website);
    script.setAttribute('data-auto-track', 'false');
    script.setAttribute('data-do-not-track', 'true');
    script.setAttribute('data-before-send', 'HNAnalyticsBeforeSend');
    script.onload = () => {
      ready = !!window.umami?.track;
      loading = false;
      if (!allowed()) { queue = []; return; }
      if (!ready) return;
      pageview();
      queue.splice(0).forEach(event => send(...event));
    };
    script.onerror = () => { loading = false; queue = []; script.remove(); };
    document.head.append(script);
  }
  document.addEventListener('visitorConsentCollected', start);
  if (window.Shopify?.customerPrivacy) start();
  else window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.2'}], error => {
    if (!error) start();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, event => {
      track('variant_selected', {variant_id: event?.data?.variant?.id});
    });
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      // Other sources also emit on removal/refresh. They are not successful adds.
      if (event?.source === 'product-form' && event.cartData && !event.cartData.status) {
        track('add_to_cart', {variant_id: event.productVariantId});
      }
    });
  }
  document.addEventListener('hn:preview-ready', () => track('preview_requested', {product_id: config.dataset.product}));
  document.addEventListener('hn:sale-alert-subscribed', () => track('sale_alert_subscribed'));
  document.addEventListener('click', event => {
    const target = event.target.closest?.('a, button, input[type="submit"]');
    if (!target || target.disabled || target.closest('[inert]')) return;
    if (target.matches('[data-product-buy-now]')) track('checkout_clicked', {source: 'buy_now'});
    else if (target.matches('[name="checkout"]')) track('checkout_clicked', {source: 'cart'});
    if (target.tagName !== 'A') return;
    let url;
    try { url = new URL(target.href, window.location.origin); } catch (_) { return; }
    if (url.origin !== window.location.origin) return;
    const path = url.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
    const destinations = [
      [/^\/collections\/books\/?$/, 'books'], [/^\/products\//, 'product'],
      [/^\/collections\//, 'collection'], [/^\/pages\/(listen|for-kids)(\/|$)/, 'audio'],
      [/^\/pages\/quiz(\/|$)/, 'quiz'], [/^\/blogs\//, 'magazine'],
      [/^\/pages\/hearts-days\/?$/, 'sales'],
    ];
    const destination = destinations.find(([pattern]) => pattern.test(path))?.[1];
    if (destination) track('navigation_clicked', {destination});
  });
})();
